/**
 * `utils/apiService` — WEB half: the axios instance and its two interceptors.
 *
 * Verbatim from `apps/web/src/utils/apiService.js` apart from the four seam substitutions
 * listed below. Mobile's half is `index.native.ts`; the two interceptors share almost nothing
 * beyond header names, so they are real platform bodies rather than a formatted split (only
 * `getErrorTextFromResponse` and `FORCED_LOGOUT_ERROR_CODES` are in `shared.ts`).
 *
 * | app source | here |
 * |---|---|
 * | `store.getState()` / `store.dispatch()` | core's own `store.getState()` / `store.dispatch()` |
 * | `ToastNotifyError` (UI — core must not import `@rista/ui`) | `effects-bridge`'s `notifyError` |
 * | `handleLogoutAction` (talks to a native Android bridge) | `effects-bridge`'s `logout` |
 * | `RISTA_API_URL` / `RECOMMENDATION_ENGINE_URL` / `REACT_APP_HEALTH_SERVICE_URL` / the `?atoken=` query param | `effects-bridge`'s `getEnv` |
 * | `startGlobalLoader()` / `stopGlobalLoader()` | `requestManager.increment()` / `.decrement()` |
 *
 * The loader substitution is not a seam at all: `helper/globalLoaderHelper`'s two functions are
 * one-line wrappers over `requestManager`, and `requestManager` is ALREADY in core as the same
 * singleton the app's `globalLoaderReduxSync` / `globalInteractionLock` subscribe to. Calling it
 * directly avoids uplifting a three-function file that adds nothing.
 *
 * 🔴 **Every injected value is read INSIDE an interceptor, never at module load.** In both apps
 * `bootstrapCoreBridges` reaches this module through its own import graph (web:
 * `helper/commonHelpers`), so this module's body runs BEFORE `initEffectsBridge()` does. A
 * module-load `getEnv()` would therefore read the un-wired defaults. The one place the app
 * evaluated config eagerly was `axios.create({ baseURL: RISTA_API_URL })`; see `api` below.
 *
 * 🔴 **Second deliberate deviation from the app source: the `If-Modified-Since` fallback is a
 * sentinel date, not `null`.** apps/web ran axios **0.27** on release_4.0.0, where `= null`
 * reached the wire as the string `"null"`. Commit `9fa77ad19` dropped `axios` from
 * `apps/web/package.json`, so under `nodeLinker: hoisted` the web bundle now resolves the copy
 * installed for THIS package (`axios ^1.19.0`) — and axios 1.x DROPS null headers instead of
 * stringifying them, which silently handed catalogue and other `Last-Modified` responses to the
 * browser's HTTP cache. See `IF_MODIFIED_SINCE_NONE` in `./shared` for the measurements and why
 * an absent header is not equivalent.
 */
import axios from "axios";
import moment from "moment";
import { commonConstants } from "@dotpe/rista-pos-common";
import store from "../../redux-store";
import { getEnv, logout, notifyError } from "../../effects-bridge";
import { requestManager } from "../requestManager";
import { getLocalStorage, setLocalStorage } from "../localStorage";
import { LOCALSTORAGE_KEYS } from "../constants";
import { ONLINE } from "../constants/shared";
import { LAST_NETWORK_ERROR } from "../../data/constants";
import { backendErrorMap } from "../backendErrors";
import { RISTA_COMMON_URL, RISTA_CUSTOM_URL } from "../../config";
import { saveRequestHeaderData } from "../../redux-store/slices/RequestHeaderSlice";
import { setNetworkStatus } from "../../redux-store/slices/LocalServerSlice";
import { isOffline, isSyncStarted } from "../../facade/ServiceFacade";
import { isEnableOfflineModeFeature } from "../../helpers/OfflineHelper";
import { showApprovalRequestModal } from "../../helpers/discountValidationFlow";
import { getUpdatedAdhocTableData } from "../../helpers/adhocTableHelpers";
import { showToastFromApi } from "../../helpers/saleHelpers";
import { isKioskModeEnabled } from "../../kiosk/config";
import {
  FORCED_LOGOUT_ERROR_CODES,
  IF_MODIFIED_SINCE_NONE,
  getErrorTextFromResponse,
  isQuietRequest,
} from "./shared";

export * from "./shared";

const defaultOptions = {
  headers: {
    "Content-Type": "application/json",
  },
};

/**
 * ⚠️ **The one deliberate deviation from the app source, and it is observably inert.** The app
 * passed `baseURL: RISTA_API_URL` to `axios.create`, evaluated at module load — which is exactly
 * when `getEnv` cannot answer (see the module note above). It is safe to drop because the
 * request interceptor below ASSIGNS `config.baseURL` on every path that reaches the network, so
 * the instance default was never the value used: the normal path overwrites it three ways, and
 * the offline path returns `config.offlineFn()`, replacing the config object outright.
 * Pinned by a runtime probe asserting the resolved `config.baseURL` for all three branches.
 */
const api = axios.create(defaultOptions);

api.interceptors.request.use((config: any) => {
  if (isOffline() && config.offlineFn) {
    if (!config.skipGlobalLoader) {
      requestManager.increment();
    }
    return config.offlineFn();
  }

  const ristaApiUrl = getEnv("ristaApiUrl");
  config.baseURL = `${ristaApiUrl}${RISTA_COMMON_URL}`;
  if (config.recommendationEngine) {
    config.baseURL = getEnv("recommendationEngineUrl");
  } else if (config.customUrl) {
    config.baseURL = `${ristaApiUrl}${RISTA_CUSTOM_URL}`;
  } else {
    config.baseURL = `${ristaApiUrl}${RISTA_COMMON_URL}`;
  }
  if (getLocalStorage("RESET_ATOKEN")) {
    config.headers.ATOKEN = getLocalStorage("RESET_ATOKEN");
  }
  // `urlAtoken` is the `?atoken=` query parameter, read off `window.location.search` ONCE at
  // bootstrap by the app and handed over as env — the app used to capture it at this module's
  // load for the same reason: it must be the value the page was opened with, not whatever the
  // SPA's history happens to hold at request time.
  const atoken = getEnv("urlAtoken");
  if (getLocalStorage("ATOKEN") || atoken) {
    config.headers.ATOKEN = getLocalStorage("ATOKEN") || atoken;
  }

  if (getLocalStorage("deviceIdentifier")) {
    config.headers.deviceIdentifier = getLocalStorage("deviceIdentifier");
  }
  if (!config.skipGlobalLoader) {
    requestManager.increment();
  }
  config.headers["Access-Control-Allow-Origin"] = "*";
  let requestHeader = store.getState().requestHeaderStore?.data ?? {};
  console.log(config.url, "=======requestHeader=======", requestHeader)
  // if (requestHeader[config.url])
  // The `: null` the app file had here is deliberately NOT preserved — under axios 1.x a null
  // header is dropped rather than stringified, which hands the request to the browser's HTTP
  // cache and yields stale-or-missing data. See IF_MODIFIED_SINCE_NONE in ./shared.
  config.headers["If-Modified-Since"] = !!requestHeader?.[config.url]
    ? requestHeader[config.url]
    : IF_MODIFIED_SINCE_NONE;

  // isNew key helps BE identify whether request is from new ui or old ui
  config.headers.isNew = true;

  // REMOVED 2026-08-19 — dead AND broken. Nothing in either app ever set `config.isLocalServer`
  // (the only other `isLocalServer*` symbols are `isLocalServerEnable`, `isLocalServerChangeOpen`
  // and LocalServerSlice's `isLocalServerApiCall` — all unrelated), and the body called
  // `getMasterDomain()`, which was neither imported nor defined in that file. Had the branch ever
  // been reached it would have thrown `ReferenceError: getMasterDomain is not defined`.
  // Its `delete config.headers` was also the one axios-1.x incompatibility here, so removing it
  // cleared that blocker too.

  function checkSignal() {
      let signal: any = "";
      if (config.url.split("?")[0] === getEnv("healthServiceUrl")){
          return signal;
      }
      return isOffline() && !isSyncStarted() ? AbortSignal.abort() : signal;
  }

  return {
    ...config,
    signal: checkSignal()
  }
}, (error: any) => {
  if (!error?.config?.skipGlobalLoader) {
    requestManager.decrement();
  }
  return Promise.reject(error);
});

api.interceptors.response.use((response: any) => {
  const quiet = isQuietRequest(response?.config);
  if (response?.data?.code && !quiet) {
    const errorText = getErrorTextFromResponse(response?.data);
    showToastFromApi(errorText);
  }
  if (response?.data?.code === commonConstants.errorKeys.REST_NEED_ORDER_APPROVAL && !quiet) {
    const sale = JSON.parse(response.config.data || '{}');
    showApprovalRequestModal({ mid: sale.mid, discountTotal: sale.discountTotal, type: response?.data?.message?.type });
  }
  let requestHeader = store.getState().requestHeaderStore?.data
    ? structuredClone(store.getState().requestHeaderStore.data)
    : {};
  if (!!response.headers?.["last-modified"] && response.config.url) {
    requestHeader[response.config.url] = response.headers?.["last-modified"];
    store.dispatch(saveRequestHeaderData(requestHeader));
  }
  if (response?.data?.code === "REST_VERSION_MISMATCH")
    getUpdatedAdhocTableData(JSON.parse(response?.config?.data || "{}"));
  if (
    (response?.data?.code && FORCED_LOGOUT_ERROR_CODES.includes(response?.data?.code))
  ) {
    // KIOSK MODE: the toast is staff language ("Session Timedout, require credentials") and
    // must not appear in front of a customer. The logout itself still runs — web's
    // `handleLogoutAction` (behind `effects-bridge.logout`) has its own kiosk branch, which
    // parks the kiosk on its dead-end screen instead of clearing to /login.
    if (!isKioskModeEnabled()) {
      notifyError(backendErrorMap[response?.data?.code as keyof typeof backendErrorMap]);
    }
    logout();
  }

  if (response?.status === 200) {
    const lastErrorTime = getLocalStorage('lastNetworkError')
    if (lastErrorTime) {
      setLocalStorage(LAST_NETWORK_ERROR, '')
    }
    store.dispatch(setNetworkStatus(ONLINE));
  }
  if (!response?.config?.skipGlobalLoader) {
    requestManager.decrement();
  }
  return response.data;
},
  function (error: any) {
    console.log(error?.code, "==============error")
    if (
      error?.response?.status === 401 ||
      error?.response?.status === 403
    ) {
      // store.dispatch(openCustomModel(true));
    } else if (
      error?.response?.status === 502 ||
      error?.response?.status === 503 ||
      error?.response?.status === 500
    ) {
      // Backend error handles by dropping message
    } else if (error?.code === "ERR_NETWORK" && isEnableOfflineModeFeature() && getLocalStorage(LOCALSTORAGE_KEYS.isOffline) !== "true") {
      if (!isQuietRequest(error?.config)) showToastFromApi(error?.code);
      const lastErrorTime = getLocalStorage('lastNetworkError')
      const currentTime = moment().format();
      if (lastErrorTime) {
        var duration = moment.duration(moment(currentTime).diff(moment(lastErrorTime))).asSeconds();
        if (duration >= 60) {
          store.dispatch(setNetworkStatus("offline"));
          setLocalStorage(LAST_NETWORK_ERROR, currentTime)
        }
      }
      else {
        setLocalStorage(LAST_NETWORK_ERROR, currentTime)
      }
    }
    if (!error?.config?.skipGlobalLoader) {
      requestManager.decrement();
    }
    return Promise.reject(error.response);
  }
);

export default api;
